import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { Badge } from "../Badge";
import { Search } from "lucide-react";

export const PATIENT_STATUSES = ["Active", "Pending", "On Hold", "Inactive", "Closed"];

export default function Patients() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/admin/patients?include_archived=${showArchived}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [showArchived]);

  const filtered = useMemo(() => {
    if (!items) return [];
    let list = [...items];
    if (q) {
      const needle = q.toLowerCase();
      list = list.filter((p) => [p.full_name, p.phone, p.email, p.address].filter(Boolean).join(" ").toLowerCase().includes(needle));
    }
    if (status) list = list.filter((p) => p.status === status);
    return list;
  }, [items, q, status]);

  if (!items) return <p className="text-slate-500 font-semibold">Loading patients…</p>;

  return (
    <div data-testid="admin-patients">
      <h1 className="font-display text-3xl md:text-4xl text-brand-navy">Patients</h1>
      <p className="mt-2 text-[15px] text-slate-600">Patient profiles are created from accepted referrals. Select a patient to view or edit their full record and care plan.</p>

      <div className="mt-6 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            data-testid="patient-search"
            type="text"
            placeholder="Search patients…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full rounded-xl border border-slate-300 bg-white pl-11 pr-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-teal/60 focus:border-brand-teal"
          />
        </div>
        <select data-testid="patient-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-teal/60">
          <option value="">All statuses</option>
          {PATIENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-600">
          <input type="checkbox" data-testid="patient-show-archived" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="h-4 w-4 accent-[#008080]" />
          Show archived
        </label>
      </div>

      <p className="mt-4 text-sm font-semibold text-slate-500">{filtered.length} patient{filtered.length === 1 ? "" : "s"}</p>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((p) => (
          <button
            key={p.id}
            data-testid={`patient-card-${p.id}`}
            onClick={() => navigate(`/portal/patients/${p.id}`)}
            className="text-left rounded-2xl border border-slate-200 bg-white p-6 hover:border-brand-teal/40 hover:shadow-[0_16px_40px_rgb(11,27,61,0.08)] transition-shadow duration-300"
          >
            <div className="flex items-start justify-between gap-3">
              <h2 className="font-display text-xl text-brand-navy">{p.full_name}</h2>
              <Badge kind="patient" value={p.status} />
            </div>
            <p className="mt-2 text-sm text-slate-600">{p.phone}</p>
            <p className="text-sm text-slate-600 truncate">{p.address}</p>
            {p.archived && <p className="mt-2 text-xs font-bold text-red-700 uppercase tracking-wide">Archived</p>}
          </button>
        ))}
        {filtered.length === 0 && (
          <div className="md:col-span-2 xl:col-span-3 rounded-2xl border border-slate-200 bg-white p-14 text-center text-slate-500">
            No patients yet. Convert an accepted referral to create the first patient profile.
          </div>
        )}
      </div>
    </div>
  );
}
